import { createContext, lazy, Suspense, useContext, useMemo, useState } from 'react';

type SharedFileProgressContextType = {
  showFileDownloadDialog: boolean;
  setShowFileDownloadDialog: (showFileDownloadDialog: boolean) => void;
};

const LazySharedFileDownloadDialog = lazy(() =>
  import(
    /* webpackChunkName: "SharedFileDownloadDialog" */
    '@unifyapps/carbon/no-code/components/SharedFileDownloadDialog/SharedFileDownloadDialog'
  ).then((m) => ({ default: m.SharedFileDownloadDialog })),
);

const SharedFileProgressContext = createContext<SharedFileProgressContextType | null>(null);

export const useSharedFileProgress = () => {
  const context = useContext(SharedFileProgressContext);
  if (!context) {
    throw new Error('useSharedFileProgress must be used within a SharedFileProgressProvider');
  }
  return context;
};

const SharedFileProgressProvider = ({ children }: { children: React.ReactNode }) => {
  const [showFileDownloadDialog, setShowFileDownloadDialog] = useState(false);

  const value = useMemo(
    () => ({ showFileDownloadDialog, setShowFileDownloadDialog }),
    [showFileDownloadDialog],
  );

  return (
    <SharedFileProgressContext.Provider value={value}>
      {children}
      {showFileDownloadDialog ? (
        <Suspense fallback={null}>
          <LazySharedFileDownloadDialog
            onClose={() => setShowFileDownloadDialog(false)}
            open={showFileDownloadDialog}
          />
        </Suspense>
      ) : null}
    </SharedFileProgressContext.Provider>
  );
};

export default SharedFileProgressProvider;
